import { type RefObject, useEffect, useRef, useState } from "react";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useSavePrintoutPresetSettings } from "../../hooks/usePrintoutPresets";
import { createPresetAutosave, type PendingPresetSave, PRINTOUT_AUTOSAVE_DELAY_MS, type PresetAutosave } from "./detailedPresetState";
import type { SessionEdits } from "./useLivePreset";

/**
 * The saved printouts' 5s autosave, wired to the save mutation, plus whether a save is on its way.
 * An edit still waiting when the department changes (or the popup unmounts) is saved, not dropped.
 */
export const usePresetAutosave = (sessionEditsRef: RefObject<SessionEdits>): { autosave: PresetAutosave; isSaving: boolean } => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const { mutateAsync: saveOnServer, isPending: isSaving } = useSavePrintoutPresetSettings();
	const saveRef = useRef(saveOnServer);
	saveRef.current = saveOnServer;

	const [autosave] = useState(() =>
		createPresetAutosave((pending: PendingPresetSave) => {
			/**
			 * Once a save has answered, the cache holds what the server kept — on success these very edits,
			 * on failure (already shown and tracked by the mutation) the last copy that did save. Either way
			 * these edits stop being "newer than the cache", so reopening the printout shows the server's
			 * copy. Unless newer edits have replaced them in the meantime: those are still on their way.
			 */
			const forgetIfLatest = (): void => {
				if (sessionEditsRef.current.get(pending.presetId) === pending.settings) sessionEditsRef.current.delete(pending.presetId);
			};
			return saveRef.current(pending).then(
				() => {
					forgetIfLatest();
					return true;
				},
				() => {
					// Already shown and tracked by `useSavePrintoutPresetSettings`; only counted here.
					forgetIfLatest();
					return false;
				},
			);
		}, PRINTOUT_AUTOSAVE_DELAY_MS),
	);

	// Keyed on the department on purpose: an edit still waiting at a department switch (or at unmount) is
	// sent now rather than dropped. By then the save hook already has the new department's options, so the
	// edit carries its own department (`PendingPresetSave.departmentId`) and is saved, and cached, there.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `departmentId` is the trigger, not an input.
	useEffect(() => () => void autosave.flush(), [autosave, departmentId]);

	return { autosave, isSaving };
};
